// --- Our Modules --------------------------------------------------------------------------------
import useLang from "@/hooks/useLang";
import useTranslation from "@/hooks/useTranslation";
import type { TyUnitHolder } from "@/types";
import { formatDate, formatMoney, pressToOpen } from "@/utils";
import Avatar from "@/UI/atoms/Avatar";
import Icon from "@/UI/atoms/Icon";
import type { TyIconName } from "@/UI/atoms/Icon/types";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
type Props = {
    holder: TyUnitHolder;
    /**
     * What the unit is listed at. The badge prints what was actually agreed, and on this one screen
     * that figure sits a scroll away from the asking price in the details — so when they differ the
     * badge says which is which rather than leaving the page contradicting itself.
     */
    listPrice?: number | null;
    /** The whole badge. It opens the paper, which is the only way into it from this screen. */
    onOpen?: () => void;
};

/**
 * Who has this unit, and on what terms.
 *
 * A unit chip that says `sold` and will not say to whom is the one thing anybody opens a sold unit
 * to find out — and until now the screen had the answer in hand (`statusLock` knew a contract
 * existed) and printed a sentence saying the status could not be edited. That is the company's
 * problem answered and the person's question ignored.
 *
 * It sits **above the tabs**, not inside one. A unit being spoken for is not a detail of the unit,
 * it is the fact that governs everything else on the screen, and it has to be true whichever tab
 * somebody happens to be standing on.
 *
 * Two shapes, one card: a contract names a price and a signing date, a reservation names a deposit
 * and a date it runs out. Both name the person.
 *
 * **It is the way into the paper.** The unit's screen has no contracts tab — at most one contract
 * and one hold are ever live on a flat, and both are named here — so pressing the badge opens the
 * record itself, with the hold that preceded it and the way through to the buyer.
 */
export default function UnitHolderCard({ holder, listPrice, onOpen }: Props) {
    const t = useTranslation();
    const lang = useLang((state) => state.lang) ?? "ar";

    const { kind, record } = holder;
    const customer = typeof record.customer === "object" && record.customer ? record.customer : null;
    const isSold = kind === "contract";

    const title = isSold ? (record.type === "rent" ? t.crm.holderRented : t.crm.holderSold) : t.crm.holderReserved;

    /**
     * Money carries the currency and a badge; a date carries a calendar. Three unlabelled numbers in
     * a row is the one thing this card cannot afford — a deposit and a price look identical until
     * you read the label, and the person reading is checking a figure, not studying the card.
     */
    /**
     * What came off the asking price. A let is not measured against a sale price, so the two are
     * only subtracted when they are the same kind of figure — anything else invents a discount.
     */
    const agreed = isSold ? record.amount : record.price;
    const isComparable = !isSold || record.type === "sale";
    const discount = isComparable && listPrice && agreed && listPrice > agreed ? listPrice - agreed : null;

    const facts: { key: string; icon: TyIconName; label: string; value: string; isMoney?: boolean; isOff?: boolean }[] = [];
    if (isSold) {
        if (record.amount)
            facts.push({ key: "amount", icon: "subscription", label: t.properties.fields.price, value: formatMoney(record.amount), isMoney: true });
        if (discount)
            facts.push({ key: "discount", icon: "stat", label: t.sales.discount, value: formatMoney(discount), isMoney: true, isOff: true });
        if (record.signedAt) facts.push({ key: "signedAt", icon: "check", label: t.crm.fields.date, value: formatDate(record.signedAt, lang) });
        if (record.endsAt) facts.push({ key: "endsAt", icon: "calendar", label: t.crm.holderUntil, value: formatDate(record.endsAt, lang) });
    } else {
        if (record.price)
            facts.push({ key: "price", icon: "subscription", label: t.properties.fields.price, value: formatMoney(record.price), isMoney: true });
        if (discount)
            facts.push({ key: "discount", icon: "stat", label: t.sales.discount, value: formatMoney(discount), isMoney: true, isOff: true });
        if (record.deposit)
            facts.push({ key: "deposit", icon: "stat", label: t.crm.holderDeposit, value: formatMoney(record.deposit), isMoney: true });
        if (record.expiresAt) facts.push({ key: "expiresAt", icon: "calendar", label: t.crm.holderUntil, value: formatDate(record.expiresAt, lang) });
    }

    return (
        <section
            className={`unit-holder unit-holder--${isSold ? "sold" : "reserved"}`}
            role={onOpen ? "button" : undefined}
            tabIndex={onOpen ? 0 : undefined}
            onClick={onOpen}
            onKeyDown={onOpen ? pressToOpen(onOpen) : undefined}
        >
            <span className="unit-holder__mark" aria-hidden="true">
                <Icon name={isSold ? "check" : "history"} size={18} />
            </span>

            <div className="unit-holder__who">
                <span className="unit-holder__title">{title}</span>

                {customer ? (
                    <span className="unit-holder__person">
                        <Avatar name={customer.name} picture={customer.picture ?? null} size={30} rounded />
                        <span className="unit-holder__person-text">
                            <span className="unit-holder__name">{customer.name}</span>
                            {/* A number reads left to right in every language. */}
                            <bdi className="unit-holder__phone" dir="ltr">
                                {customer.phone}
                            </bdi>
                        </span>
                    </span>
                ) : (
                    <span className="unit-holder__name">{t.crm.notSet}</span>
                )}
            </div>

            {facts.length > 0 && (
                <dl className="unit-holder__facts">
                    {facts.map((fact) => (
                        <div key={fact.key} className={`unit-holder__fact${fact.isOff ? " unit-holder__fact--off" : ""}`}>
                            <span className="unit-holder__fact-icon">
                                <Icon name={fact.icon} size={14} />
                            </span>

                            <dt className="unit-holder__fact-label">{fact.label}</dt>
                            <dd className={`unit-holder__fact-value${fact.isOff ? " unit-holder__fact-value--off" : ""}`}>
                                <bdi dir="ltr">{fact.value}</bdi>
                                {fact.isMoney && <span className="unit-holder__fact-unit">{t.properties.currency}</span>}
                            </dd>
                        </div>
                    ))}
                </dl>
            )}
        </section>
    );
}
